import type { BannerVariant, ButtonVariant } from '@getgo/chameleon-core'
import type { Testable } from '../../common'
export interface BannerLink {
  readonly text: string
  readonly href?: string
  readonly onClick?: () => void
}

export interface WindowReferencedNotification {
  /**
   * By default display Banner/SnackBar/Modal will be shown
   * on ALL windows/tabs.
   * Unless you specify a `target` of where you want it displayed.
   *
   * https://jira.ops.expertcity.com/browse/SCORE-1291
   */
  readonly target?: Window
}

/**
 * @summary Banner notification data
 * @description
 *      Banner notification interface contains the following properties:
 *      description: Mandatory property. Short message displayed to the user
 *      variant: Mandatory property. Variation of the Notification being displayed
 *      closable: Optional property. Allows the user to close the Notification
 *      icon: Optional property. Displays a SVG icon
 *      onClose: Optional property. Function to be executed when the Banner notification is closed
 */
export interface BannerNotification extends WindowReferencedNotification {
  // TODO: unit test for this should create a fresh mock instead
  /* eslint-disable functional/prefer-readonly-type */
  id?: string
  description: string
  closable?: boolean
  variant?: BannerVariant
  onClose?: () => void
  icon?: string
  link?: BannerLink
  /* eslint-enable functional/prefer-readonly-type */
}

export interface BannerNotificationWithId extends BannerNotification {
  readonly id: string
}

/**
 * @summary Snackbar button action data
 * @description
 *      Snackbar notification interface contains the following properties:
 *      label: The button label
 *      handler: The action handler
 */
export interface SnackbarNotificationAction {
  readonly label: string
  readonly handler: () => void
}

/**
 * @summary Snackbar notification data
 * @description
 *      Snackbar notification interface contains the following properties:
 *      id: Mandatory property. The unique id of the snackbar
 *      title: Mandatory property. Short message displayed to the user
 *      message: Optional property. Short message under the title
 *      progress: Optional property. The percentage of progress
 *      actions: Optional property. The possible feedback actions for the user
 */
export interface SnackbarNotification extends WindowReferencedNotification, Testable {
  // eslint-disable-next-line functional/prefer-readonly-type
  id: string
  readonly title: string
  readonly message?: string
  // eslint-disable-next-line functional/prefer-readonly-type
  progress?: number
  readonly actions?: readonly SnackbarNotificationAction[]
  /**
   * list of functions to cancel hiding after a few seconds.
   *
   * Since we might run the same widget on different window "realms", we get different
   * setTimeout Id per realm, so we need a place to store those "cancel tokens"
   */
  // eslint-disable-next-line functional/prefer-readonly-type
  timeoutCancelers?: (() => void)[]
}

/**
 * @summary Modal button action data
 * @description
 *      Modal notification interface contains the following properties:
 *      label: The button label
 *      handler: The action handler
 */
export interface ModalNotificationActions {
  readonly label: string
  readonly handler: () => void
  readonly variant?: ButtonVariant
}

/**
 * @summary Modal notification data
 * @description
 *      Modal notification interface contains the following properties:
 *      title: Mandatory property. Short message displayed to the user
 *      content: Mandatory property. Short message under the title
 *      actions: Optional property. The possible feedback actions for the user
 */
export interface ModalNotification extends WindowReferencedNotification {
  // eslint-disable-next-line functional/prefer-readonly-type
  id: string
  readonly title: string
  readonly content: string
  readonly actions?: readonly ModalNotificationActions[]
  readonly closable?: boolean
}

/**
 * This is a temporary fix to allow banners with approved ids to be shown on the Shell.
 * This array is hierarchical, the banner whose id has the lowest index will be shown.
 */
export const approvedShellBannerIds = [
  'overrideBanner',
  'retry-login-container',
  'shellDisconnectionBanner',
  'phone-disconnected-error-id',
  'shellUnrecoverableErrorBanner',
  'shellSessionExpiredBanner',
  'direct-meeting-invite-declined-banner',
]
